import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fetchProducts, getImageUrl } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import { searchAll, isSearchable, highlight } from '../utils/siteSearch';
import './SiteSearch.css';

// Search bar that drops down under the header (opened by the search icon after the cart icon).
// After 3 letters it suggests matching products, pages and articles; the match can be in ANY detail
// (name, brand, model, category, description, specs, colours …). Enter / "See all results" opens the
// products page filtered by the same words.

// products + blog posts are loaded once (and refreshed every few minutes), the first time the bar opens
let cache = null;
let inflight = null;
const CACHE_MS = 5 * 60 * 1000;
function loadSearchData() {
  if (cache && Date.now() - cache.at < CACHE_MS) return Promise.resolve(cache);
  if (!inflight) {
    inflight = Promise.all([
      fetchProducts().catch(() => []),
      api.get('/blog').then(r => r.data).catch(() => []),
    ]).then(([products, posts]) => {
      cache = { products: Array.isArray(products) ? products : [], posts: Array.isArray(posts) ? posts : [], at: Date.now() };
      return cache;
    }).finally(() => { inflight = null; });
  }
  return inflight;
}

const SearchIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const prettyCategory = (key) => String(key || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function Marked({ text, query }) {
  return <>{highlight(text, query).map((part, i) => (part.hit ? <b key={i}>{part.text}</b> : <React.Fragment key={i}>{part.text}</React.Fragment>))}</>;
}

export default function SiteSearch({ open, onClose, menu }) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState(cache);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);

  // While the bar is open: on phones lock the page behind it, hide the floating chat / WhatsApp buttons
  // (they sat on top of the results), and tell the CSS how far down the screen the bar starts so the
  // results fill exactly the space left above the on-screen keyboard.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('ss-open');
    const phone = () => window.innerWidth <= 768;
    const prevOverflow = document.body.style.overflow;
    if (phone()) document.body.style.overflow = 'hidden';
    const root = document.documentElement;
    const measure = () => {
      const el = panelRef.current;
      if (el) root.style.setProperty('--ss-top', `${Math.max(0, Math.round(el.getBoundingClientRect().top))}px`);
    };
    measure();
    const vv = window.visualViewport;
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    vv?.addEventListener('resize', measure);
    return () => {
      document.body.classList.remove('ss-open');
      document.body.style.overflow = prevOverflow;
      root.style.removeProperty('--ss-top');
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      vv?.removeEventListener('resize', measure);
    };
  }, [open]);

  // open: load data and focus; close: reset
  useEffect(() => {
    if (!open) { setQuery(''); setDebounced(''); setActive(-1); return; }
    loadSearchData().then(setData);
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  // wait a moment after typing before searching
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(query); setActive(-1); }, 120);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const searchable = isSearchable(debounced);
  const results = useMemo(
    () => (data && searchable ? searchAll({ products: data.products, posts: data.posts, menu }, debounced) : { products: [], pages: [], posts: [], total: 0 }),
    [data, searchable, debounced, menu]
  );

  // one flat list so the arrow keys can move through every suggestion
  const items = useMemo(() => {
    const list = [];
    results.products.forEach(p => list.push({ kind: 'product', key: `p-${p._id || p.model}`, to: `/products/${p.category}/${p.model || p._id}`, p }));
    results.pages.forEach(pg => list.push({ kind: 'page', key: `g-${pg.path}`, to: pg.path, pg }));
    results.posts.forEach(po => list.push({ kind: 'post', key: `a-${po.slug}`, to: `/blog/${po.slug}`, po }));
    if (results.total > 0) list.push({ kind: 'all', key: 'all', to: `/products?q=${encodeURIComponent(debounced.trim())}` });
    return list;
  }, [results, debounced]);

  const go = (to) => { onClose(); navigate(to); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (items.length ? (a + 1) % items.length : -1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (items.length ? (a <= 0 ? items.length - 1 : a - 1) : -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && items[active]) go(items[active].to);
      else if (isSearchable(query)) go(`/products?q=${encodeURIComponent(query.trim())}`);
    }
  };

  // keep the highlighted suggestion in view while arrowing through a long list
  useEffect(() => {
    if (active < 0) return;
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const row = (item, idx, content) => (
    <a
      key={item.key}
      href={item.to}
      data-idx={idx}
      role="option"
      aria-selected={active === idx}
      className={`ss-item ss-${item.kind}${active === idx ? ' active' : ''}`}
      onClick={(e) => { e.preventDefault(); go(item.to); }}
      onMouseEnter={() => setActive(idx)}
    >
      {content}
    </a>
  );
  let idx = -1;

  return (
    <>
      <div className="ss-backdrop" onClick={onClose} aria-hidden="true" />
      <section className="ss" ref={panelRef} role="search" aria-label="Search the site">
        <div className="ss-inner">
          <div className="ss-field">
            <div className="ss-input-wrap">
            <span className="ss-field-icon"><SearchIcon size={20} /></span>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search products, brands, pages, articles…"
              role="combobox"
              aria-label="Search"
              aria-autocomplete="list"
              aria-controls="ss-listbox"
              aria-expanded={searchable && results.total > 0}
              autoComplete="off"
              spellCheck="false"
              maxLength={80}
              enterKeyHint="search"
              inputMode="search"
            />
            {query && <button type="button" className="ss-clear" onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Clear search">&times;</button>}
            </div>
            <button type="button" className="ss-close" onClick={onClose}>Close</button>
          </div>

          <div className="ss-results" id="ss-listbox" ref={listRef} role="listbox">
            {!isSearchable(query) && (
              <p className="ss-hint">Search by name, brand, model, category, colour or any detail.</p>
            )}

            {isSearchable(query) && !data && <p className="ss-hint">Searching…</p>}

            {searchable && data && results.total === 0 && (
              <div className="ss-empty">
                <p>No matches for “<strong>{debounced.trim()}</strong>”.</p>
                <p className="ss-hint">Check the spelling, try fewer words, or browse:</p>
                <div className="ss-quick">
                  <a href="/products?category=electric-scooters" onClick={e => { e.preventDefault(); go('/products?category=electric-scooters'); }}>Electric Scooters</a>
                  <a href="/products?category=electric-bikes" onClick={e => { e.preventDefault(); go('/products?category=electric-bikes'); }}>Electric Bikes</a>
                  <a href="/contact" onClick={e => { e.preventDefault(); go('/contact'); }}>Ask us</a>
                </div>
              </div>
            )}

            {results.products.length > 0 && (
              <>
                <div className="ss-group">Products</div>
                {results.products.map(p => {
                  idx += 1;
                  const item = items[idx];
                  const img = p.images?.[0] || p.image;
                  const old = Number(p.oldPrice) > Number(p.price);
                  return row(item, idx, (
                    <>
                      <span className="ss-thumb">{img && <img src={getImageUrl(img, 120)} alt="" width="52" height="52" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />}</span>
                      <span className="ss-text">
                        <span className="ss-title"><Marked text={p.name} query={debounced} /></span>
                        <span className="ss-meta">{[p.brand, prettyCategory(p.category)].filter(Boolean).join(' · ')}{p.inStock === false ? ' · Out of stock' : ''}</span>
                      </span>
                      <span className="ss-price">{old && <s>{currency}{Number(p.oldPrice).toFixed(2)}</s>}{currency}{Number(p.price || 0).toFixed(2)}</span>
                    </>
                  ));
                })}
              </>
            )}

            {results.pages.length > 0 && (
              <>
                <div className="ss-group">Pages &amp; categories</div>
                {results.pages.map(pg => {
                  idx += 1;
                  return row(items[idx], idx, (
                    <>
                      <span className="ss-icon" aria-hidden="true">&rarr;</span>
                      <span className="ss-text"><span className="ss-title"><Marked text={pg.title} query={debounced} /></span>{pg.category && <span className="ss-meta">{pg.category}</span>}</span>
                    </>
                  ));
                })}
              </>
            )}

            {results.posts.length > 0 && (
              <>
                <div className="ss-group">Articles</div>
                {results.posts.map(po => {
                  idx += 1;
                  return row(items[idx], idx, (
                    <>
                      <span className="ss-thumb ss-thumb-post">{po.image && <img src={getImageUrl(po.image, 120)} alt="" width="52" height="52" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />}</span>
                      <span className="ss-text"><span className="ss-title"><Marked text={po.title} query={debounced} /></span>{po.excerpt && <span className="ss-meta">{String(po.excerpt).replace(/<[^>]*>/g, ' ').slice(0, 90)}</span>}</span>
                    </>
                  ));
                })}
              </>
            )}

            {results.total > 0 && (() => {
              idx += 1;
              return row(items[idx], idx, <span className="ss-all-text">See all results for “{debounced.trim()}” &rarr;</span>);
            })()}
          </div>
        </div>
      </section>
    </>
  );
}
